import { useCallback, useState } from 'react';
import { useTranslation } from '../../../../../utils/useTranslation';
import { SmartDrawer } from '../../../../feedback/SmartDrawer';
import { Button } from '../../../../input/Button';
import { Box } from '../../../../layout/Box';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Flex } from '../../../../layout/Flex';
import { Typography } from '../../../../typography/Typography';
import { SOURCE_OPTIONS, STATUSES_OPTIONS } from '../../constants';
import { FeedManagementDateDrawer } from '../FeedManagementDateDrawer/FeedManagementDateDrawer';
import { PERIOD_OPTIONS } from '../FeedManagementDateDropdown/constants';
import { getDateDropdownPlaceholder } from '../FeedManagementDateDropdown/utils';
import { FeedManagementFilter } from '../FeedManagementFilter/FeedManagementFilter';
import { FeedManagementFiltersDrawerProps } from './types';

export const FeedManagementFiltersDrawer: React.FC<FeedManagementFiltersDrawerProps> = ({
    source,
    status,
    period,
    dateFrom,
    dateTo,
    onChange,
    onClose,
}) => {
    const t = useTranslation();
    const [selectedSource, setSelectedSource] = useState(source);
    const [selectedStatus, setSelectedStatus] = useState(status);
    const [selectedPeriod, setSelectedPeriod] = useState(period);
    const [selectedDateFrom, setSelectedDateFrom] = useState(dateFrom);
    const [selectedDateTo, setSelectedDateTo] = useState(dateTo);

    const [visibleDatePopup, setVisibleDatePopup] = useState(false);

    const onSelectSource = useCallback((value: string | null) => {
        setSelectedSource(value);
    }, []);
    const onSelectStatus = useCallback((value: string | null) => {
        setSelectedStatus(value);
    }, []);
    const onSelectPeriod = useCallback((value: string | null) => {
        setSelectedPeriod(value);

        setSelectedDateFrom(null);
        setSelectedDateTo(null);
    }, []);
    const onSelectDate = useCallback((dateFromValue?: string, dateToValue?: string) => {
        setSelectedDateFrom(dateFromValue || null);
        setSelectedDateTo(dateToValue || null);

        setVisibleDatePopup(false);
    }, []);

    const onChoiceDateClick = useCallback(() => setVisibleDatePopup(true), []);

    const onSubmit = useCallback(() => {
        onChange({
            source: selectedSource,
            status: selectedStatus,
            period: selectedPeriod,
            dateFrom: selectedDateFrom,
            dateTo: selectedDateTo,
        });
        onClose();
    }, [
        onChange,
        onClose,
        selectedDateFrom,
        selectedDateTo,
        selectedPeriod,
        selectedSource,
        selectedStatus,
    ]);

    if (visibleDatePopup) {
        return (
            <FeedManagementDateDrawer
                dateFrom={selectedDateFrom}
                dateTo={selectedDateTo}
                onChange={onSelectDate}
                onClose={onClose}
            />
        );
    }

    return (
        <SmartDrawer
            dataTestId={DataTestId.FEED_MANAGEMENT_FILTERS_DRAWER}
            onClose={onClose}
            titleContent={
                <Typography variant="title3" weight="bold">
                    {t('Фильтры')}
                </Typography>
            }
            mainContent={() => (
                <Flex spacing={2} width="100%">
                    <FeedManagementFilter
                        title={t('Источники')}
                        value={selectedSource}
                        options={SOURCE_OPTIONS}
                        onChange={onSelectSource}
                    />
                    <FeedManagementFilter
                        title={t('Статусы')}
                        value={selectedStatus}
                        options={STATUSES_OPTIONS}
                        onChange={onSelectStatus}
                    />
                    <FeedManagementFilter
                        title={t('Даты')}
                        value={selectedPeriod}
                        options={PERIOD_OPTIONS}
                        additionalOption={
                            <Button
                                size="m"
                                color={selectedDateFrom || selectedDateTo ? 'primary' : 'secondary'}
                                onClick={onChoiceDateClick}
                                whiteSpace="nowrap"
                            >
                                {getDateDropdownPlaceholder(
                                    'Выбрать даты',
                                    selectedDateFrom,
                                    selectedDateTo,
                                )}
                            </Button>
                        }
                        onChange={onSelectPeriod}
                    />
                    <Box pt={2} width="100%">
                        <Button size="l" width="100%" onClick={onSubmit}>
                            {t('Применить')}
                        </Button>
                    </Box>
                </Flex>
            )}
        />
    );
};
